import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { Image } from '@scorenco/components';
import { Types } from '@scorenco/core';

import { GetPostsDocument } from './Facebook.query.generated';

const style = {
  root: css`
    position: relative;
    .club-infos {
      margin-left: 22rem;
      margin-top: 2rem;
      display: flex;
      flex-direction: row;
      align-items: center;
      h1 {
        font-size: 3rem;
        margin: 0;
        margin-bottom: -0.6rem;
      }
      h2 {
        font-size: 2.5rem;
        font-weight: 400;
        font-style: unset;
        margin: 0;
        margin-bottom: 0rem;
      }
      .likes-club {
        font-size: 1.5rem;
        display: flex;
        flex-direction: row;
        align-items: center;
        img {
          width: 2.25rem;
        }
      }
    }

    .join-fb {
      display: flex;
      flex-direction: row;
      align-items: center;
      position: absolute;
      right: 2.5rem;
      top: 1rem;
      span {
        font-size: 1.5rem;
        color: #1e75fc;
        margin-right: 0.3rem;
      }
    }

    .posts-container {
      padding: 4rem 6rem;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-start;
      .post {
        /* padding: unset; */
        width: 40rem;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        margin: 1.5rem;
        height: 100%;
        padding: 0 1rem;
        font-size: 3rem;
        hr {
          width: 100%;
        }
        span {
          color: #00274a;
          padding: 0 1rem;
        }
        img {
          width: 40rem;
          max-height: 30rem;
          object-fit: cover;
          margin-bottom: 1rem;
        }
        .date {
          text-align: right;
          color: #00274155;
          margin-bottom: 0.55rem;
          font-size: 0.85rem;
          width: 100%;
        }
        .content {
          margin-bottom: 0.7rem;
          width: 100%;
          font-size: 1.5rem;
        }
        .statistics {
          display: flex;
          align-items: center;
          flex-direction: row;
          justify-content: space-between;
          color: #00274a;
          margin: 0.75rem 0;
          width: 100%;
          .likes {
            display: flex;
            align-items: center;
            flex-direction: row;
            &:before {
              content: '';
              background: url(facebook/like-2.svg) no-repeat;
              background-size: contain;
              width: 1.05rem;
              height: 1.05rem;
              margin-right: 0.25rem;
            }
          }
        }
        .comment {
          height: 6.5rem;
          display: flex;
          flex-direction: row;
          width: 100%;
          align-items: center;
          position: relative;
          padding: 0.3rem 0.5rem;
          img {
            border-radius: 100%;
            width: 3rem;
            height: 3rem;
          }
          .comment-text {
            display: flex;
            flex-direction: column;
            font-size: 1.85rem;
            line-height: 0.95rem;
            .user {
              font-weight: bold;
              margin-bottom: 0.1rem;
            }
          }
          .comment-number {
            position: absolute;
            bottom: 0.2rem;
            right: 0;
            color: #00274a;
            font-size: 1.85rem;
          }
        }
        &:nth-child(1) {
          animation: fadein 0.8s ease-in-out;
        }
        &:nth-child(2) {
          animation: fadein 0.8s ease-in-out;
        }
        &:nth-child(3) {
          animation: fadein 0.8s ease-in-out;
        }
      }
    }
    @keyframes fadein {
      0% {
        opacity: 0;
        transform: scale(0.9);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }
  `,
};

type FacebookProps = { clubHouse: Types.Clubhouse_Club_House };

const getPostStat = (
  postAdditional: unknown,
  key: 'likes' | 'comments' | 'shares'
) => {
  if (typeof postAdditional !== 'object' || postAdditional === null) {
    return 0;
  }

  const value = (postAdditional as Record<string, unknown>)[key];
  return typeof value === 'number' ? value : 0;
};

export const Facebook = ({ clubHouse }: FacebookProps) => {
  const clubId = clubHouse.club?.id;
  const getPosts = useQuery(GetPostsDocument, {
    variables: {
      club_id: clubId ?? 0,
    },
    skip: !clubId,
    context: {
      role: 'anonymous',
    },
  });

  const posts = getPosts.data?.socials_post ?? [];
  const firstPost = posts[0];

  if (posts.length < 1) {
    return (
      <Typography
        variant="h2"
        gutterBottom
        style={{ marginTop: '20rem', textAlign: 'center' }}
      >
        Aucun Facebook n'est associé au Club-house, merci de contacter
        Score'n'co.
      </Typography>
    );
  }

  return (
    <Box css={style.root}>
      <Box className="club-infos">
        {firstPost?.user_pic && (
          <Image
            src={firstPost.user_pic}
            style={{
              borderRadius: '100%',
              width: '5rem',
              height: '5rem',
              marginRight: '1rem',
            }}
          />
        )}
        <Box>
          <Typography
            variant="h1"
            gutterBottom
            style={{ marginBottom: '0.5rem' }}
          >
            {firstPost?.user_screenname}
          </Typography>

          <Box className="likes-club">
            <Image src="/images/facebook/like.svg" />
            <Typography>
              Aimez la page {firstPost?.user_screenname} sur Facebook
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* <Box className="join-fb">
        <Typography>Rejoignez-nous sur</Typography>
        <Image src="/images/facebook/fb.svg" />
      </Box> */}
      <Box className="posts-container">
        {posts.map((post, idx) => {
          return (
            <Paper className="post" variant="bordered" key={idx}>
              {post.image_url && <Image src={post.image_url} />}
              {/* <Typography className="date">
                {new Date(post.post_timestamp).toLocaleDateString()}
              </Typography> */}
              <Typography className="content">
                {post.post_text?.replace(/<[^>]+>/g, '') ?? ''}
              </Typography>
              <Divider
                sx={{
                  opacity: '0.6',
                }}
              />
              <Box className="statistics">
                <Typography className="likes" sx={{ fontSize: '1.25rem' }}>
                  {getPostStat(post.post_additional, 'likes')}
                </Typography>
                <Typography className="comments" sx={{ fontSize: '1.25rem' }}>
                  {getPostStat(post.post_additional, 'comments') +
                    ' commentaires'}
                </Typography>
                <Typography className="shares" sx={{ fontSize: '1.25rem' }}>
                  {getPostStat(post.post_additional, 'shares') + ' partages'}
                </Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};
